/**
 * CCAM Zod Validation Schemas
 *
 * Schemas for runtime validation of CCAM-related data
 */
import { z } from "zod/v4";
import { PatientsIdSchema } from "../zodSchemas.js";
/**
 * Schema for CCAMActe
 */
export const CCAMActeSchema = z.object({
    code: z.string().min(1, "Code CCAM requis"),
    libelle: z.string().min(1, "Libellé requis"),
    acteType: z.string().min(1, "Type d'acte requis"),
    tarif: z.number().nonnegative("Tarif doit être positif ou nul"),
    dateEffet: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, "Format de date invalide (YYYY-MM-DD)"),
    dateFin: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, "Format de date invalide (YYYY-MM-DD)")
        .nullable(),
    ccamVersion: z.string(),
    isExpired: z.boolean().optional(),
    allowsExtraFees: z.boolean().optional(),
});
/**
 * Schema for getByCode input
 */
export const CCAMGetByCodeInputSchema = z.object({
    code: z.string().min(1, "Code CCAM requis"),
});
/**
 * Schema for search filters
 */
export const CCAMSearchFiltersSchema = z.object({
    acteType: z.array(z.string()).optional(),
    excludeExpired: z.boolean().optional().default(true),
});
/**
 * Schema for search input
 */
export const CCAMSearchInputSchema = z.object({
    query: z.string().min(1, "Texte de recherche requis"),
    filters: CCAMSearchFiltersSchema.optional(),
    page: z
        .number()
        .int()
        .min(1, "Numéro de page doit être >= 1")
        .optional()
        .default(1),
    cursor: z.number().optional(),
    limit: z
        .number()
        .int()
        .min(1)
        .max(100, "Maximum 100 résultats par page")
        .optional()
        .default(50),
});
/**
 * Schema for pagination metadata
 */
export const CCAMPaginationMetadataSchema = z.object({
    currentPage: z.number().int().min(1),
    totalPages: z.number().int().nonnegative(),
    totalCount: z.number().int().nonnegative(),
    pageSize: z.number().int().min(1),
});
/**
 * Schema for search result
 */
export const CCAMSearchResultSchema = z.object({
    actes: z.array(CCAMActeSchema),
    pagination: CCAMPaginationMetadataSchema,
});
/**
 * Schema for CCAM favorite acte
 */
export const CCAMFavoriteActeSchema = z.object({
    id: z.string().min(1),
    ccamCode: z.string().min(1),
    ccamLabel: z.string(),
    allowsExtraFees: z.boolean(),
    customLabel: z.string().min(1),
    customFees: z.number().nonnegative().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CCAMUpsertFavoriteInputSchema = z.object({
    ccamCode: z.string().min(1, "Code CCAM requis"),
    customLabel: z.string().trim().min(1, "Intitulé personnalisé requis"),
    customFees: z.number().nonnegative("Honoraires invalides").nullable(),
});
export const CCAMDeleteFavoriteInputSchema = z.object({
    ccamCode: z.string().min(1, "Code CCAM requis"),
});
export const CCAMFavoriteActesListSchema = z.array(CCAMFavoriteActeSchema);
const dentistAssemblySchema = z.enum(["screw_retained", "cement_retained"]);
const dentistCrownMaterialSchema = z.enum([
    "pfm",
    "metal",
    "monolithic_zirconia",
    "veneered_zirconia",
    "other_ceramic",
]);
const toothZoneSchema = z.enum([
    "incisor_canine_first_premolar",
    "second_premolar",
    "molar",
    "all",
]);
const coreSchema = z.enum([
    "none",
    "composite",
    "composite with prefab post",
    "cast metal",
    "other laboratory core",
]);
const tempCrownSchema = z.enum(["direct", "indirect", "none"]);
const teethNumberTypeSchema = z.enum(["unary", "multiple", "none"]);
export const DentistUnaryImplantSchema = z.object({
    root: z.literal("implantology"),
    brand: z.string().trim().min(1, "Marque requise"),
    model: z.string().trim().min(1, "Modèle requis"),
    type: z.literal("unary_implant"),
    teeth_number_type: teethNumberTypeSchema,
    abutment: z.enum(["standard", "custom", "none"]),
    assembly: dentistAssemblySchema,
    crown_material: dentistCrownMaterialSchema,
});
export const DentistBridgeImplantSchema = z.object({
    root: z.literal("implantology"),
    brand: z.string().trim().min(1, "Marque requise"),
    model: z.string().trim().min(1, "Modèle requis"),
    type: z.literal("bridge_implant"),
    teeth_number_type: teethNumberTypeSchema,
    abutment: z.enum(["standard", "custom", "conical", "other", "none"]),
    assembly: dentistAssemblySchema,
    crown_material: dentistCrownMaterialSchema,
    number_of_units: z.number().int().positive("Nombre d'unités invalide"),
});
export const CrownOnToothSchema = z.object({
    root: z.literal("prosthodontics"),
    type: z.literal("crown_on_tooth"),
    teeth_number_type: teethNumberTypeSchema,
    endo: z.boolean(),
    core: coreSchema,
    temp_crown: tempCrownSchema,
    tooth_zone: toothZoneSchema,
    crown_material: dentistCrownMaterialSchema,
});
export const DentistActesSchema = z.discriminatedUnion("type", [
    DentistUnaryImplantSchema,
    DentistBridgeImplantSchema,
    CrownOnToothSchema,
]);
export const AssistantFavoriteQuoteLineSchema = z.object({
    description: z.string(),
    ccam_code: z.string(),
    teeth: z.array(z.number()),
    fees: z.string(),
});
export const AssistantFavoriteActeSchema = z.object({
    id: z.string().min(1),
    label: z.string().trim().min(1),
    acte: DentistActesSchema,
    quoteLines: z.array(AssistantFavoriteQuoteLineSchema).default([]),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const AssistantUpsertFavoriteActeInputSchema = z.object({
    id: z.string().min(1).optional(),
    label: z.string().trim().min(1, "Nom du favori requis"),
    acte: DentistActesSchema,
    quoteLines: z.array(AssistantFavoriteQuoteLineSchema).default([]),
});
export const AssistantDeleteFavoriteActeInputSchema = z.object({
    id: z.string().min(1, "Identifiant requis"),
});
export const AssistantFavoriteActesListSchema = z.array(AssistantFavoriteActeSchema);
export const CreatePatientQuoteLineInputSchema = z.object({
    fees: z.number().nonnegative("Honoraires invalides"),
    tx: z.number().min(0, "Taux invalide").max(100, "Taux invalide"),
});
export const CreatePatientQuoteFavoriteInputSchema = z.object({
    favoriteId: z.string().min(1, "Favori requis"),
    teeth: z.array(z.number().int().positive()).default([]),
    quoteLines: z
        .array(CreatePatientQuoteLineInputSchema)
        .min(1, "Au moins une ligne est requise par favori"),
});
export const CreatePatientQuoteInputSchema = z.object({
    patientId: PatientsIdSchema,
    name: z.string().trim().optional(),
    favorites: z
        .array(CreatePatientQuoteFavoriteInputSchema)
        .min(1, "Au moins une ligne est requise"),
});
//# sourceMappingURL=CCAMSchemas.js.map